/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export const GRADUATE_ATTRIBUTES = [
  {
    id: 'attendance',
    label: 'Attendance, Appearance, and Punctuality',
    expectations: [
      'Arrives on time and prepared for work',
      'Provides sufficient notice if unable to report for work',
      'Clean, neat appearance',
      'Dresses appropriately for position and duties',
      'Wears ID Badge in accordance with policy',
      'Practices personal hygiene for position and duties'
    ]
  },
  {
    id: 'initiative',
    label: 'Initiative, Motivation, Self-Direction',
    expectations: [
      'Participates fully in tasks or projects from start to finish',
      'Initiates interaction with supervisor for next task or project upon successful completion of previous one',
      'Able to work with little supervision; dependable',
      'Use good judgement'
    ]
  },
  {
    id: 'communication',
    label: 'Communication',
    expectations: [
      'Communicates effectively, orally and in writing, using the language and vocabulary appropriate to a variety of audiences within the workplace including coworkers, supervisors, and customers',
      'Demonstrates active listening skills; focuses attentively, makes eye contact or other affirming gestures, confirms understanding and follows directions'
    ]
  },
  {
    id: 'teamwork',
    label: 'Teamwork and Collaboration',
    expectations: [
      'Works productively with co-workers, individually and in teams; support organization\'s mission and goals',
      'Accepts direction and constructive feedback with positive attitude',
      'Cooperative'
    ]
  },
  {
    id: 'critical_thinking',
    label: 'Critical Thinking and Problem Solving',
    expectations: [
      'Notices and identifies challenges and problems that arise in the workplace',
      'Brings concerns to attention of supervisors',
      'Develops solutions to challenges and problems by analyzing available information and looking at options, guided by expectations for organization'
    ]
  },
  {
    id: 'policy_integrity',
    label: 'Workplace Policy, Integrity, Culture and Safety',
    expectations: [
      'Exhibits understanding of workplace culture and policy',
      'Follows professional standards for use of computers, phones and social media',
      'Respects confidentiality',
      'Complies with health and safety rules for the workplace',
      'Adheres to cell phone usage policy'
    ]
  },
  {
    id: 'knowing_how_to_learn',
    label: 'Knowing How to Learn',
    expectations: [
      'Eager and willing to learn',
      'Listens and observes to learn',
      'Asks questions',
      'Initiates to seek answers',
      'Inquisitive',
      'Manages time effectively'
    ]
  },
  {
    id: 'info_tech',
    label: 'Information/Media & Technology',
    expectations: [
      'Knows how to locate necessary information to perform tasks',
      'Uses digital media literacy to identify, access and analyze',
      'Displays global knowledge with use of technology'
    ]
  },
  {
    id: 'global_perspective',
    label: 'Global Perspective',
    expectations: [
      'Identifies and respects cultural diversity',
      'Understands social/economic relationship impact on the job',
      'Seeks to work towards conflict resolution',
      'Thinks globally and understands link to people worldwide'
    ]
  },
  {
    id: 'creativity_innovation',
    label: 'Creativity/Innovation',
    expectations: [
      'Introduces new ideas or methods for task or work process',
      'Imaginative: Thinks outside the box',
      'Shares ideas and insights',
      'Encourages engagement'
    ]
  }
];

export const VOCATIONAL_SKILLS = [
  // Career/Engagement Skills
  {
    id: 'adaptability',
    label: 'Adaptability',
    definition: 'Adjusts to new conditions, handles changes with a positive attitude, and learns new tasks quickly.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'collecting_organizing',
    label: 'Collecting and Organizing Information',
    definition: 'Systematically gathers, structures, and logs data or resource materials for easy retrieval and analysis.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'creativity',
    label: 'Creativity',
    definition: 'Generates innovative ideas, develops unique solutions, and designs engaging approaches to tasks or projects.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'professionalism',
    label: 'Professionalism',
    definition: 'Demonstrates dependable work habits, punctuality, polite communication, and adheres to workplace standards.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'leadership',
    label: 'Leadership',
    definition: 'Guides and motivates others toward achieving common goals, taking initiative and ownership of responsibilities.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'project_management',
    label: 'Project Management',
    definition: 'Plans, schedules, and executes tasks step-by-step to achieve specific goals on time.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'public_speaking',
    label: 'Public Speaking/Presentations',
    definition: 'Communicates ideas clearly, confidently, and persuasively to individuals or groups.',
    category: 'Career/Engagement Skills'
  },
  {
    id: 'understanding_aspects',
    label: 'Understanding Aspects',
    definition: 'Comprehends various parts, details, and contexts of a project or business operation.',
    category: 'Career/Engagement Skills'
  },

  // Digital Literacy Skills
  {
    id: 'computer_tech',
    label: 'Computer Technology',
    definition: 'Demonstrates proficiency in operating essential hardware and navigating software platforms.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'database_usage',
    label: 'Database Usage',
    definition: 'Effectively enters, manages, and retrieves data from structured systems to maintain accurate records.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'graphic_design',
    label: 'Graphic Design',
    definition: 'Creates visual concepts to communicate ideas that inspire, inform, and captivate audiences.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'media_literacy',
    label: 'Media Literacy',
    definition: 'Accesses, analyzes, and evaluates message content across various media formats and channels.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'office_suite',
    label: 'Office Suite Software',
    definition: 'Uses standard productivity applications (word processors, slide decks, etc.) to complete tasks.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'coding',
    label: 'Coding',
    definition: 'Writes, edits, and debugs source code to implement software functions and instructions.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'software_development',
    label: 'Software Development',
    definition: 'Designs, builds, and maintains software solutions using structured engineering processes.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'spreadsheet_usage',
    label: 'Spreadsheet Usage',
    definition: 'Inputs formulas, organizes financial or numerical data, and creates tables/charts.',
    category: 'Digital Literacy Skills'
  },
  {
    id: 'web_development',
    label: 'Web Development (or industry specific technology)',
    definition: 'Builds and updates websites or configures professional tools specific to the industry.',
    category: 'Digital Literacy Skills'
  },

  // Applied Academic Skills
  {
    id: 'applied_mathematics',
    label: 'Applied Mathematics',
    definition: 'Applies math concepts, calculations, and formulas to solve real-world workplace problems.',
    category: 'Applied Academic Skills'
  },
  {
    id: 'reading',
    label: 'Reading',
    definition: 'Comprehends and extracts key insights from technical manuals, guidelines, and business documents.',
    category: 'Applied Academic Skills'
  },
  {
    id: 'research_analysis',
    label: 'Research and Analysis',
    definition: 'Systematically investigates topics to discover facts, identify patterns, and draw logical conclusions.',
    category: 'Applied Academic Skills'
  },
  {
    id: 'writing',
    label: 'Writing',
    definition: 'Drafts clear, concise, and professional emails, reports, and documentation.',
    category: 'Applied Academic Skills'
  },
  {
    id: 'multiple_languages',
    label: 'Multiple Languages',
    definition: 'Communicates effectively in more than one language to support diverse clients or teams.',
    category: 'Applied Academic Skills'
  },

  // STEM-Related Skills
  {
    id: 'analyze',
    label: 'Analyze',
    definition: 'Examines complex information, processes, or data sets to understand their component parts.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'engineering_concepts',
    label: 'Engineering Concepts',
    definition: 'Understands and applies scientific and practical principles to design and build structures or systems.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'technology_driven',
    label: 'Technology Driven',
    definition: 'Leverages modern tools, automated processes, and advanced tech to optimize workflows.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'science_concepts',
    label: 'Science Concepts',
    definition: 'Understands and explains natural, biological, physical, or chemical principles relevant to tasks.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'cause_effect_relations',
    label: 'Cause/Effect relations',
    definition: 'Identifies how actions, processes, or changes lead to specific outcomes or consequences.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'predict_conclude',
    label: 'Predict/Conclude',
    definition: 'Forecasts future outcomes based on current data and draws sound, evidence-based conclusions.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'repair_equipment',
    label: 'Repair equipment',
    definition: 'Diagnoses issues and restores tools, machinery, or devices to proper working order.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'uses_software_effectively',
    label: 'Uses software effectively',
    definition: 'Operates complex application programs efficiently to perform specialized tasks.',
    category: 'STEM-Related Skills'
  },
  {
    id: 'mathematics_concepts',
    label: 'Mathematics concepts',
    definition: 'Understands fundamental algebraic, geometric, or statistical concepts for problem-solving.',
    category: 'STEM-Related Skills'
  }
];
